/**
 * ModalEmpleadosFaltantes.js
 *
 * Crea DESHABILITADOS a los ex-empleados que la lista de raya necesita y que no
 * existen en Biwo, a partir del Excel de empleados del software externo (el mismo
 * layout de "Importar Desde Software Externo").
 *
 * El cruce vive en empleadosFaltantes.js (puro) y el mapeo del layout en
 * mapeoExterno.js: aquí solo se lee el archivo, se pinta y se envía.
 */

import React, { Component } from 'react'
import { message, notification, Upload, Alert, Tag, Tooltip, Table } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import axios from 'axios'
import { connect } from 'react-redux'
import moment from 'moment'
import * as XLSX from 'xlsx'

import { ModalItem, ButtonItem } from '../../../../Components/Items'
import { NOMBRE_HOJA } from '../../Empleados/ImportarEmpleadosExterno/mapeoExterno'
import { cruzarConArchivoExterno } from './empleadosFaltantes'

const dinero = (n) =>
  (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// mapearFilaExterna deja las fechas como 'YYYY-MM-DD'.
const fechaArchivo = (v) => (v ? moment(v, 'YYYY-MM-DD').format('DD/MM/YYYY') : '—')

const ESTADO_ARCHIVO = { archivos: [], filas: null, resultado: null, creando: false }

class ModalEmpleadosFaltantes extends Component {
  state = { ...ESTADO_ARCHIVO, catalogos: null, cargandoCatalogos: false }

  componentDidMount() {
    if (this.props.visible) this.cargarCatalogos()
  }

  componentDidUpdate(prevProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({ ...ESTADO_ARCHIVO })
      if (!this.state.catalogos) this.cargarCatalogos()
    }
  }

  // Los mismos catálogos que ImportarEmpleadosExterno: mapearFilaExterna resuelve con
  // ellos tipo de contrato, periodo, prestación, banco, registro patronal, etc.
  cargarCatalogos() {
    this.setState({ cargandoCatalogos: true })
    axios
      .post(
        'empleados/datosParaImportarEmpleados',
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState(
            {
              catalogos: {
                tiposContrato:       res.data.tiposContrato || [],
                formasDePago:        res.data.formasDePago || [],
                turnos:              res.data.turnos || [],
                tiposPeriodo:        res.data.tiposPeriodo || [],
                tiposPrestacion:     res.data.tiposPrestacion || [],
                bancos:              res.data.bancos || [],
                registrosPatronales: res.data.registrosPatronales || [],
                tiposRegimen:        res.data.tiposRegimen || [],
                estados:             res.data.estados || [],
              },
              cargandoCatalogos: false,
            },
            this.cruzar
          )
        } else {
          message.error(res.data.message || 'Error al cargar los catálogos de empleados')
          this.setState({ cargandoCatalogos: false })
        }
      })
      .catch(() => {
        message.error('Error al cargar los catálogos de empleados')
        this.setState({ cargandoCatalogos: false })
      })
  }

  // La lectura tiene que ser IGUAL a ImportarEmpleadosExterno.cargarArchivoExcel:
  // cellDates + raw:false con dateNF es lo que deja las fechas en 'YYYY-MM-DD' para
  // parsearFechaExterna. Si cambia allá, cambia aquí.
  onSubirArchivo({ file, onSuccess }) {
    const lector = new FileReader()
    lector.onload = (e) => {
      try {
        const libro = XLSX.read(new Uint8Array(e.target.result), { type: 'array', cellDates: true })
        const hoja = libro.Sheets[NOMBRE_HOJA]
        if (!hoja) {
          message.error(`No se encontró la hoja "${NOMBRE_HOJA}" en el archivo. Debe ser el Excel de empleados del software externo.`)
          this.setState({ archivos: [{ uid: '1', name: file.name, status: 'error' }], filas: null, resultado: null })
          return
        }
        const filas = XLSX.utils.sheet_to_row_object_array(hoja, { raw: false, dateNF: 'YYYY-MM-DD' })
        this.setState({ filas, archivos: [{ uid: '1', name: file.name, status: 'done' }] }, this.cruzar)
        onSuccess('ok')
      } catch (err) {
        console.error('[empleadosFaltantes] error al leer el Excel:', err)
        message.error('No se pudo leer el archivo. Verifica que sea un .xlsx válido.')
      }
    }
    lector.readAsArrayBuffer(file)
  }

  cruzar = () => {
    const { filas, catalogos } = this.state
    if (!filas || !catalogos) return
    this.setState({
      resultado: cruzarConArchivoExterno({
        filas,
        catalogos,
        faltantes: this.props.faltantes,
        empleados: this.props.empleados,
      }),
    })
  }

  crear() {
    const { resultado, creando } = this.state
    if (creando || !resultado?.aCrear.length) return

    this.setState({ creando: true })
    axios
      .post(
        'empleados/importarEmpleadosDeshabilitados',
        { empresaId: this.props.empresaId, data: resultado.aCrear.map((r) => r.emp) },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ creando: false })
        if (res.data.success !== 2) {
          message.error(res.data.message || 'No se pudieron crear los empleados')
          return
        }
        const { creados = [], omitidos = [], fallidos = [] } = res.data
        const problemas = [...omitidos, ...fallidos]
        if (problemas.length) {
          notification.warning({
            message: `${problemas.length} empleado(s) no se crearon`,
            description: (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {problemas.map((p, i) => <li key={i}><b>{p.codigo}</b>: {p.motivo}</li>)}
              </ul>
            ),
            duration: null,
          })
        }
        if (creados.length) {
          message.success(`Se crearon ${creados.length} empleado(s) deshabilitado(s)`)
          this.props.onCreados()
        }
      })
      .catch(() => {
        this.setState({ creando: false })
        message.error('Error al crear los empleados')
      })
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  columnasACrear() {
    return [
      { title: 'Código', dataIndex: 'codigo', key: 'codigo', width: 70 },
      { title: 'Nombre', dataIndex: 'nombre', key: 'nombre', width: 240 },
      { title: 'RFC', dataIndex: 'rfc', key: 'rfc', width: 130 },
      {
        title: 'Estatus externo', key: 'estatus', width: 120,
        render: (_, r) => {
          if (r.esBaja) return <Tag>Baja</Tag>
          const reingreso = String(r.estatusExterno || '').trim().toUpperCase() === 'R'
          return (
            <Tooltip title="Está activo en el sistema externo y aquí va a quedar deshabilitado. Si sigue trabajando, habilítalo desde Empleados en vez de volver a importarlo: el importador de Empleados no lo detectaría como repetido.">
              <Tag color="orange">{reingreso ? 'Reingreso' : 'Activo'}</Tag>
            </Tooltip>
          )
        },
      },
      {
        title: 'Fecha alta', key: 'alta', width: 100,
        render: (_, r) => fechaArchivo(r.emp.fechaAlta),
      },
      {
        title: (
          <Tooltip title="Solo informativa: el empleado se crea deshabilitado sin fecha de baja, para que el cálculo de la nómina histórica no le recorte días.">
            <span>Baja externa</span>
          </Tooltip>
        ),
        key: 'baja', width: 100,
        render: (_, r) => fechaArchivo(r.fechaBajaExterna),
      },
      {
        title: 'Salario diario', key: 'sd', width: 110, align: 'right',
        render: (_, r) => <span style={{ fontFamily: 'monospace' }}>{dinero(r.emp.salarioDiario)}</span>,
      },
    ]
  }

  listaDeProblemas(titulo, lista, texto) {
    if (!lista.length) return null
    return (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 500, fontSize: 13 }}>
          {titulo} <Tag color="red">{lista.length}</Tag>
        </div>
        <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 12, maxHeight: 120, overflowY: 'auto' }}>
          {lista.map((f) => <li key={f.codigo}><b>{f.codigo}</b> — {texto(f)}</li>)}
        </ul>
      </div>
    )
  }

  render() {
    const { visible, onCerrar, faltantes } = this.props
    const { archivos, resultado, creando, cargandoCatalogos } = this.state
    const aCrear = resultado?.aCrear || []

    return (
      <ModalItem
        title="Crear empleados faltantes"
        visible={visible}
        onCancel={onCerrar}
        footer={null}
        width={980}
        spinning={cargandoCatalogos || creando}
      >
        <Alert
          type="info"
          showIcon
          style={{ margin: '12px 0 16px' }}
          message={`La lista de raya trae ${faltantes.length} empleado(s) que no existen en Biwo`}
          description={
            <span>
              Sube el Excel de empleados de tu software externo (hoja <b>{NOMBRE_HOJA}</b>, el mismo de
              <i> Importar Desde Software Externo</i>). Se crean <b>solo</b> los que necesita esta lista
              de raya, y todos quedan <b>deshabilitados</b>: no entran a las nóminas normales. Si alguno
              vuelve a trabajar, usa <i>Habilitar</i> en Empleados.
            </span>
          }
        />

        <Upload
          showUploadList={{ showRemoveIcon: false, showPreviewIcon: false }}
          fileList={archivos}
          customRequest={this.onSubirArchivo.bind(this)}
          accept=".xlsx"
        >
          <ButtonItem icon={<UploadOutlined />}>Subir Excel de empleados</ButtonItem>
        </Upload>

        {resultado && (
          <>
            {resultado.avisosCatalogo.length > 0 && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
                message="Valores que no existen en los catálogos de la empresa"
                description={
                  <>
                    <div style={{ marginBottom: 6 }}>Los empleados se crean de todos modos, con estos campos vacíos.</div>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {resultado.avisosCatalogo.map((g) => (
                        <li key={g.catalogo}>
                          <b>{g.catalogo}:</b>{' '}
                          {g.valores.map((v) => `${v.valor} (${v.filas})`).join(', ')}
                        </li>
                      ))}
                    </ul>
                  </>
                }
              />
            )}

            <h4 style={{ marginTop: 16 }}>
              Se van a crear <Tag color="green">{aCrear.length}</Tag>
            </h4>
            {/* Table de antd y no TableItem, por lo mismo que en ImportarNominaHistorica:
                aquí se quiere ver la lista completa sin paginar. */}
            <Table
              size="small"
              rowKey="codigo"
              dataSource={aCrear}
              columns={this.columnasACrear()}
              pagination={false}
              scroll={{ y: 300 }}
            />

            {this.listaDeProblemas(
              'No vienen en el archivo de empleados',
              resultado.noEnArchivo,
              (f) => `${f.nombreArchivo} (renglón ${f.filaExcel} de la lista de raya)`
            )}
            {this.listaDeProblemas(
              'Ya existe un empleado con ese RFC — no se crean',
              resultado.rfcExistente,
              (f) => `${f.nombre} · ${f.rfc}. ${f.motivo}`
            )}
            {this.listaDeProblemas(
              'Con errores en el archivo de empleados — no se crean',
              resultado.conErrores,
              (f) => `${f.nombre} (fila ${f.numFila}): ${f.motivo}`
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '20px 0 12px' }}>
          <ButtonItem onClick={onCerrar}>Cancelar</ButtonItem>
          <ButtonItem
            type="primary"
            disabled={!aCrear.length || creando}
            loading={creando}
            onClick={() => this.crear()}
          >
            {`Crear ${aCrear.length} empleado(s) deshabilitado(s)`}
          </ButtonItem>
        </div>
      </ModalItem>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
})

export default connect(mapStateToProps)(ModalEmpleadosFaltantes)
